import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Modal from "react-modal";
import CommonStructured from "../../common-components/CommonStructured";
import ReactVortoxSpinner from "../../common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import SubCategoryForm from "./SubCategoryForm";
import BulkSubCategoryForm from "./BulkSubCategoryForm";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { fetchApiData } from "../../../redux/reducers/commonApiSlice";
import { commonDeleteApi } from "../../../redux/reducers/deleteApiSlice";
import moment from "moment";

const SubCategories = ({
  token,
  selectedCategory,
  selectedIssueType,
  setSelectedCategory,
}) => {
  const dispatch = useDispatch();
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [subCategoriesData, setSubCategoriesData] = useState({
    data: [],
    pagination: {},
  });

  const iniModvalues = {
    isModOpen: false,
    cardName: "",
    rowData: "",
  };
  const [modValues, setModValues] = useState(iniModvalues);

  const [filters, setFilters] = useState({
    search: "",
    limit: pageLimit || 10,
    page: isPage + 1,
    ...(selectedCategory?.uid && { category_id: selectedCategory.uid }),
  });

  const fetchSubCategories = async (filters) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        limit: filters.limit,
        page: filters.page,
        ...(filters.search && { search: filters.search }),
        ...(filters.category_id && { category_id: filters.category_id }),
      }).toString();

      const result = await dispatch(
        fetchApiData({
          endpoint: `/ticketSystem/sub-category?${queryParams}`,
          token: token,
        })
      );

      if (result?.payload?.status) {
        setSubCategoriesData({
          data: result.payload.data || [],
          pagination: result.payload.pagination || {},
        });
      } else {
        toast.error("Failed to fetch sub categories");
      }
    } catch (error) {
      console.error("Error fetching sub categories:", error);
      toast.error("Failed to fetch sub categories");
    } finally {
      setLoading(false);
    }
  };

  // Update filters when page or limit changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      page: isPage + 1,
      limit: pageLimit,
    }));
  }, [isPage, pageLimit]);

  useEffect(() => {
    if (token) {
      const updatedFilters = {
        ...filters,
        ...(selectedCategory?.uid && { category_id: selectedCategory.uid }),
      };
      setFilters(updatedFilters);
      fetchSubCategories(updatedFilters);
    }
  }, [selectedCategory, isPage, token]);

  const onCloseModal = () => {
    setModValues(iniModvalues);
  };

  // console.log("subCategoriesData", subCategoriesData);

  const handleDelete = async (filter) => {
    if (filter?.uid) {
      const handleConfirm = async () => {
        try {
          const result = await dispatch(
            commonDeleteApi({
              TOKEN: token,
              endpoint: `/ticketSystem/sub-category/delete`,
              queryParams: { uid: filter.uid },
            })
          );

          if (result?.payload?.status) {
            toast.success(
              result?.payload?.message || "Sub category deleted successfully!"
            );
            fetchSubCategories(filters);
          } else {
            toast.error(
              result?.payload?.message || "Cannot delete sub category!"
            );
          }
        } catch (error) {
          toast.error(error || "Failed to delete sub category");
        }
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure you want to delete this sub category?`
      );
    } else {
      toast.error("UID required!");
    }
  };

  const subCategoriesColumns = [
    {
      dataField: "name",
      text: <p className="table__header">Sub Category Name</p>,
      formatter: (cell, row) => {
        return (
          <div className="tb-data minWidth200 table__Text">
            <p
              style={{
                margin: 0,
                fontWeight: "600",
                textTransform: "capitalize",
              }}
            >
              {row?.name || "NA"}
            </p>
          </div>
        );
      },
    },
    {
      dataField: "category_name",
      text: <p className="table__header">Category</p>,
      formatter: (cell, row) => {
        return (
          <div className="tb-data minWidth150 table__Text">
            <p style={{ margin: 0, textTransform: "capitalize" }}>
              {row?.category_name || "NA"}
            </p>
          </div>
        );
      },
    },
    {
      dataField: "status",
      text: <p className="table__header">Status</p>,
      formatter: (cell, row) => {
        return (
          <span
            className="tb-data minWidth90 withPadding borderRdius27"
            style={{
              background: row?.status === "active" ? "#379C00" : "#D90000",
              color: "#fff",
            }}
          >
            {row?.status || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "date_added",
      text: <p className="table__header">Date Added</p>,
      formatter: (cell, row) => {
        return (
          <p className="tb-data table__Text">
            {row?.date_added
              ? moment(row.date_added).format("DD-MM-YYYY")
              : "-"}
          </p>
        );
      },
    },
    {
      dataField: "action",
      text: "Action",
      formatter: (cell, row) => (
        <div className="actionButtonContainer">
          <button
            className="actionBtn icon edit"
            onClick={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                cardName: "edit",
                rowData: row,
              }))
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
            >
              <path
                d="M3.62891 17.3603V20.3254C3.62891 20.5985 3.84349 20.8131 4.1166 20.8131H7.08175C7.20855 20.8131 7.33535 20.7643 7.42313 20.6668L18.0743 10.0254L14.4166 6.36775L3.77521 17.0091C3.67768 17.1067 3.62891 17.2237 3.62891 17.3603ZM20.9029 7.19682C20.9933 7.10658 21.065 6.9994 21.114 6.88141C21.1629 6.76341 21.1881 6.63692 21.1881 6.50918C21.1881 6.38143 21.1629 6.25495 21.114 6.13695C21.065 6.01896 20.9933 5.91177 20.9029 5.82154L18.6205 3.53915C18.5302 3.44873 18.4231 3.37699 18.3051 3.32805C18.1871 3.2791 18.0606 3.25391 17.9328 3.25391C17.8051 3.25391 17.6786 3.2791 17.5606 3.32805C17.4426 3.37699 17.3354 3.44873 17.2452 3.53915L15.4603 5.32409L19.1179 8.98176L20.9029 7.19682Z"
                fill="url(#paint0_linear_14023_4477)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_14023_4477"
                  x1="3.62891"
                  y1="11.6344"
                  x2="21.1881"
                  y2="11.6344"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.0447915" stopColor="#B4000B" />
                  <stop offset="1" stopColor="#DD0000" />
                </linearGradient>
              </defs>
            </svg>
          </button>
          <button
            className="actionBtn icon delete"
            onClick={() => {
              const deleteFilter = { uid: row?.uid || "" };
              handleDelete(deleteFilter);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
            >
              <path
                d="M8.49846 4.3041H11.4978C11.4978 3.90637 11.3398 3.52492 11.0585 3.24369C10.7773 2.96245 10.3958 2.80445 9.99811 2.80445C9.60038 2.80445 9.21893 2.96245 8.9377 3.24369C8.65646 3.52492 8.49846 3.90637 8.49846 4.3041ZM7.4987 4.3041C7.4987 3.64121 7.76203 3.00548 8.23076 2.53675C8.69949 2.06802 9.33522 1.80469 9.99811 1.80469C10.661 1.80469 11.2967 2.06802 11.7655 2.53675C12.2342 3.00548 12.4975 3.64121 12.4975 4.3041H17.4963C17.6289 4.3041 17.7561 4.35676 17.8498 4.45051C17.9435 4.54425 17.9962 4.6714 17.9962 4.80398C17.9962 4.93655 17.9435 5.0637 17.8498 5.15745C17.7561 5.25119 17.6289 5.30386 17.4963 5.30386H16.4426L15.2489 15.6454C15.1645 16.3764 14.8143 17.0509 14.265 17.5405C13.7156 18.0302 13.0054 18.3007 12.2696 18.3008H7.72664C6.99077 18.3007 6.2806 18.0302 5.73125 17.5405C5.18191 17.0509 4.83173 16.3764 4.74735 15.6454L3.55363 5.30386H2.49988C2.3673 5.30386 2.24016 5.25119 2.14641 5.15745C2.05267 5.0637 2 4.93655 2 4.80398C2 4.6714 2.05267 4.54425 2.14641 4.45051C2.24016 4.35676 2.3673 4.3041 2.49988 4.3041H7.4987ZM8.99834 8.30315C8.99834 8.17057 8.94568 8.04343 8.85193 7.94968C8.75819 7.85593 8.63104 7.80327 8.49846 7.80327C8.36588 7.80327 8.23874 7.85593 8.14499 7.94968C8.05125 8.04343 7.99858 8.17057 7.99858 8.30315V14.3017C7.99858 14.4343 8.05125 14.5615 8.14499 14.6552C8.23874 14.7489 8.36588 14.8016 8.49846 14.8016C8.63104 14.8016 8.75819 14.7489 8.85193 14.6552C8.94568 14.5615 8.99834 14.4343 8.99834 14.3017V8.30315ZM11.4978 7.80327C11.3652 7.80327 11.238 7.85593 11.1443 7.94968C11.0505 8.04343 10.9979 8.17057 10.9979 8.30315V14.3017C10.9979 14.4343 11.0505 14.5615 11.1443 14.6552C11.238 14.7489 11.3652 14.8016 11.4978 14.8016C11.6303 14.8016 11.7575 14.7489 11.8512 14.6552C11.945 14.5615 11.9976 14.4343 11.9976 14.3017V8.30315C11.9976 8.17057 11.945 8.04343 11.8512 7.94968C11.7575 7.85593 11.6303 7.80327 11.4978 7.80327Z"
                fill="black"
              />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  // console.log("selectedCategory", selectedCategory);

  return (
    <div className="tab-section">
      <Modal
        isOpen={modValues.isModOpen}
        onRequestClose={onCloseModal}
        contentLabel="Modal"
        ariaHideApp={false}
      >
        {modValues.cardName === "bulk" ? (
          <BulkSubCategoryForm
            token={token}
            onCloseModal={onCloseModal}
            selectedCategory={selectedCategory}
            refetchData={() => fetchSubCategories(filters)}
          />
        ) : (
          <SubCategoryForm
            token={token}
            onCloseModal={onCloseModal}
            isEdit={modValues.cardName === "edit"}
            rowData={modValues.rowData}
            selectedCategory={selectedCategory}
            refetchData={() => fetchSubCategories(filters)}
          />
        )}
      </Modal>

      <div className="tab-section-header">
        <div>
          <h3 className="tab-section-title">Sub Categories</h3>
          <p className="tab-section-description">
            Manage sub categories for categories
          </p>
        </div>
      </div>

      {selectedCategory && (
        <div className="hierarchy-breadcrumb">
          <span className="breadcrumb-item">Issue Type:</span>
          <span className="breadcrumb-item">{selectedIssueType?.name}</span>
          <span className="breadcrumb-separator">→</span>
          <span className="breadcrumb-item">Category:</span>
          <span className="breadcrumb-current">{selectedCategory.name}</span>
          <button
            onClick={() => setSelectedCategory(null)}
            style={{
              marginLeft: "12px",
              background: "none",
              border: "none",
              color: "#dc2626",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Clear Filter
          </button>
        </div>
      )}

      {!selectedCategory && (
        <div
          style={{
            background: "#fef3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "20px",
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0, color: "#856404" }}>
            Please select a Category from the previous tab to view and manage
            sub categories.
          </p>
        </div>
      )}

      {selectedCategory && (
        <>
          <div className="bulk-add-section">
            <h4 className="bulk-add-title">Bulk Add Sub Categories</h4>
            <p className="bulk-add-description">
              Add multiple sub categories at once for faster setup
            </p>
            <button
              className="btn btn-outline-primary"
              onClick={() =>
                setModValues((prevs) => ({
                  ...prevs,
                  isModOpen: true,
                  cardName: "bulk",
                }))
              }
            >
              + Bulk Add Sub Categories
            </button>
          </div>

          <CommonStructured
            pageHeading={""}
            createBtnTxt={"+ Add Sub Category"}
            onClickCreateBtn={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                cardName: "add",
              }))
            }
            isReload={() => fetchSubCategories(filters)}
          />
          <br />

          <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table">
            {loading ? (
              <ReactVortoxSpinner />
            ) : (
              <PaymentLinkTable
                tableColumns={subCategoriesColumns}
                tableData={subCategoriesData?.data}
                tablePagination={subCategoriesData?.pagination}
                isPage={isPage}
                setIsPage={(value) => setIsPage(value)}
                setPageLimit={setPageLimit}
                pageLimit={pageLimit}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SubCategories;
